import { Platform } from 'react-native';

/**
 * Safe haptics wrapper (Wave 61). Every call is a silent no-op when the
 * native module isn't available — web, and any APK built before
 * expo-haptics rode a native build — so this is OTA-safe to ship ahead
 * of the binary that actually carries the vibration motor hookup.
 *
 * Usage philosophy (matches the no-nagging design language): haptics
 * CONFIRM, they never demand. Light tick for taps that open or pick,
 * success buzz for completions and saves. Nothing for errors — the
 * error UI already carries the weight, and an angry buzz reads as
 * shame, which Homestead doesn't do.
 */

type HapticsModule = typeof import('expo-haptics');

let cached: HapticsModule | null | undefined;

function mod(): HapticsModule | null {
  if (cached !== undefined) return cached;
  if (Platform.OS === 'web') {
    cached = null;
    return cached;
  }
  try {
    // Lazy require so builds without the native module never throw at
    // import time — the first call just resolves to the no-op path.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    cached = require('expo-haptics') as HapticsModule;
  } catch {
    cached = null;
  }
  return cached;
}

/** Light tick — opening a sheet, picking an option, tapping the FAB. */
export function hapticTap(): void {
  try {
    const h = mod();
    void h?.impactAsync(h.ImpactFeedbackStyle.Light);
  } catch {
    // Decoration only — never let a haptic break the action.
  }
}

/** Firmer bump — a long-press taking hold, entering selection mode. */
export function hapticHold(): void {
  try {
    const h = mod();
    void h?.impactAsync(h.ImpactFeedbackStyle.Medium);
  } catch {
    // no-op
  }
}

/** Success buzz — task done, record saved, scan filed. */
export function hapticSuccess(): void {
  try {
    const h = mod();
    void h?.notificationAsync(h.NotificationFeedbackType.Success);
  } catch {
    // no-op
  }
}
